import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import Spinner from "./Spinner";
import { SERVER_BASE_URL } from "../config";

function AddWorkerModal({ isOpen, onClose, token, onCreated }) {
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [inviteeName, setInviteeName] = useState("");
  const [workerRole, setWorkerRole] = useState(""); // Новое состояние для роли
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  if (!isOpen) return null;

  // Функция проверки, содержит ли email "@" и "."
  const isEmailValid = inviteeEmail.includes("@") && inviteeEmail.includes(".");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEmailValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    // Сброс ошибки перед отправкой
    setEmailError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/worker_invitations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invitee_name: inviteeName,
          invitee_email: inviteeEmail,
          worker_role: workerRole || undefined, // Отправляем роль только если она выбрана
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to create worker invitation");
      }

      const data = await response.json();
      toast.success("Worker invitation created and email sent successfully!");

      // Передаем данные о созданном приглашении обратно в родительский компонент
      if (onCreated) {
        onCreated(data);
      }
      
      // Сбрасываем форму после успешной отправки
      setInviteeEmail("");
      setInviteeName("");
      setWorkerRole("");
    } catch (error) {
      console.error(error);
      // Если сервер сообщает, что такой email уже существует,
      // сохраняем ошибку в состоянии, чтобы отобразить её в модальном окне
      if (error.message === "User with this email already exists") {
        setEmailError(error.message);
      }
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[400px] relative">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Invite a Staff Member</h2>

        {isSubmitting ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Spinner centered={false} className="mb-4" />
            <p className="text-gray-600">Sending invitation...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">Email:</label>
              <input
                type="email"
                value={inviteeEmail}
                onChange={(e) => {
                  setInviteeEmail(e.target.value);
                  setEmailError("");
                }}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              {emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">Name (optional):</label>
              <input
                type="text"
                value={inviteeName}
                onChange={(e) => setInviteeName(e.target.value)}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* Поле для выбора роли */}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">Role (optional):</label>
              <select
                value={workerRole}
                onChange={(e) => setWorkerRole(e.target.value)}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select a role</option>
                <option value="DOOR">DOOR</option>
                <option value="MARKETING">MARKETING</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Assigning a role now will set the default role when the staff member accepts the invitation.
              </p>
            </div>
            
            <div className="flex justify-center space-x-4 mt-6">
              <button
                type="submit"
                disabled={!isEmailValid || isSubmitting}
                className={`bg-indigo-600 text-white px-6 py-2 rounded-md transition duration-200 font-medium 
                  hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Invite
              </button>
              <button
                type="button"
                className="bg-white text-gray-700 px-6 py-2 rounded-md hover:bg-gray-100 transition duration-200 border border-gray-300 font-medium"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition duration-200"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

AddWorkerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  onCreated: PropTypes.func,
};

export default AddWorkerModal;